import apiClient from '../shared/apiClient';
import { 
  roleDefinitionsByClient,
  getRoleDefinition
} from 'graphql/queries';

/**
 * Service for managing role definitions
 */
class RoleService {
  
  /**
   * Get available roles for location assignments
   * @param {string} clientId - Client ID
   * @returns {Promise<Array>} Array of role definitions for locations
   */
  async getLocationRoles(clientId) {
    try {
      const result = await apiClient.query(roleDefinitionsByClient, {
        clientId: clientId,
        filter: {
          isActive: { eq: true },
          assignmentLevel: { eq: 'LOCATION_LEVEL' }
        },
        limit: 100
      });

      const roles = result?.data?.roleDefinitionsByClient?.items || [];

      // Sort alphabetically by roleName
      return roles.sort((a, b) =>
        (a.roleName || '').localeCompare(b.roleName || '')
      );
    } catch (error) {
      console.error('Error fetching location roles:', error);
      return [];
    }
  }

  /**
   * Get available roles for org unit assignments
   * @param {string} clientId - Client ID
   * @param {string} orgLevel - Org level (ORGLEVEL_1, ORGLEVEL_2, ORGLEVEL_3)
   * @returns {Promise<Array>} Array of role definitions for org units
   */
  async getOrgUnitRoles(clientId, orgLevel) {
    try {
      if (!orgLevel) {
        console.error('[RoleService] Invalid org level:', orgLevel);
        return [];
      }

      console.log('[RoleService] Fetching org unit roles for:', {
        clientId,
        orgLevel
      });

      // Query for roles with matching assignmentLevel
      const result = await apiClient.query(roleDefinitionsByClient, {
        clientId: clientId,
        filter: {
          isActive: { eq: true },
          assignmentLevel: { eq: orgLevel }
        },
        limit: 100
      });

      const roles = result?.data?.roleDefinitionsByClient?.items || [];

      // Log what we found for debugging
      console.log('[RoleService] Found roles:', {
        totalCount: roles.length,
        roles: roles.map(r => ({
          roleName: r.roleName,
          assignmentLevel: r.assignmentLevel,
          category: r.category
        }))
      });

      // If no roles found for this level
      if (roles.length === 0) {
        console.warn(`[RoleService] No roles found for assignmentLevel ${orgLevel}. This might indicate:
          1. No roles exist for this level in RoleDefinition table
          2. Consider creating roles for this organizational level`);
      }

      // Sort alphabetically by roleName
      return roles.sort((a, b) =>
        (a.roleName || '').localeCompare(b.roleName || '')
      );
    } catch (error) {
      console.error('Error fetching org unit roles:', error);
      return [];
    }
  }

  /**
   * Get all active roles for a client
   * @param {string} clientId - Client ID
   * @returns {Promise<Array>} Array of all role definitions
   */
  async getAllRoles(clientId) {
    try {
      const result = await apiClient.query(roleDefinitionsByClient, {
        clientId: clientId,
        filter: {
          isActive: { eq: true }
        },
        limit: 100
      });

      const roles = result?.data?.roleDefinitionsByClient?.items || [];
      
      // Sort alphabetically by roleName
      return roles.sort((a, b) => 
        (a.roleName || '').localeCompare(b.roleName || '')
      );
    } catch (error) {
      console.error('Error fetching all roles:', error);
      return [];
    }
  }

  /**
   * Get a specific role by ID
   * @param {string} roleId - Role definition ID
   * @returns {Promise<Object|null>} Role definition or null
   */
  async getRoleById(roleId) {
    try {
      const result = await apiClient.query(getRoleDefinition, {
        id: roleId
      });

      return result?.data?.getRoleDefinition || null;
    } catch (error) {
      console.error('Error fetching role by ID:', error);
      return null;
    }
  }
}

export default new RoleService();